if (!window.Eurus.loadedScript.has('collection-loadmore.js')) {
  window.Eurus.loadedScript.add('collection-loadmore.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      // Alpine component for collection "Load More" with de-duping
      Alpine.data(
        'xCollectionLoadMore',
        (sectionId, startPage, totalPages) => ({
          sectionId,
          currentPage: startPage,
          totalPages,
          loading: false,

          // Track unique group keys (from data-group-key)
          seenKeys: new Set(),
          renderedCount: 0,
          totalUnique: null, // Set after prefetch (or immediately if single page)
          ready: false,
          prefetchedDocs: {},

          // Initialize on mount
          init() {
            // Collect unique keys from the first page
            const initialNodes = document.querySelectorAll(
              '#items-grid .grid-item[data-group-key]',
            );
            initialNodes.forEach((node) => {
              const groupKey = node.getAttribute('data-group-key');
              if (groupKey) this.seenKeys.add(groupKey);
            });

            this.renderedCount = this.seenKeys.size;

            if (this.totalPages <= 1) {
              // Single page -> totals known immediately
              this.totalUnique = this.renderedCount;
              this.ready = true;
              this.dispatchCounts();
              return;
            }

            // Multi-page -> prefetch all pages to compute true total
            this.computeTotalUnique();
          },

          extractGroupKeys(doc) {
            const keys = new Set();
            const nodes = doc.querySelectorAll(
              '#items-grid .grid-item[data-group-key]',
            );

            nodes.forEach((node) => {
              const groupKey = node.getAttribute('data-group-key');
              if (groupKey) {
                keys.add(groupKey);
              }
            });
            return keys;
          },

          // Whether "Load More" button should be visible
          get hasMore() {
            if (this.totalUnique == null) {
              // fall back to server-side pagination before totals is known
              return this.currentPage < this.totalPages;
            }

            // Show unseen uniques (if any remaining) after totals is known
            return (
              this.renderedCount < this.totalUnique &&
              this.currentPage < this.totalPages + 1
            );
          },

          // Prefetch all pages to compute unique product count
          async computeTotalUnique() {
            const allKeys = new Set(this.seenKeys);

            for (let pageIndex = 1; pageIndex <= this.totalPages; pageIndex++) {
              const url = new URL(window.location.href);
              url.searchParams.set('page', pageIndex);
              url.searchParams.set('section_id', this.sectionId);

              try {
                const response = await fetch(url.toString());
                const html = await response.text();
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const keysFromDoc = this.extractGroupKeys(doc);

                this.prefetchedDocs[pageIndex] = doc;

                keysFromDoc.forEach((key) => {
                  allKeys.add(key);
                });
              } catch (error) {
                console.error(
                  'Count prefetch failed on page',
                  pageIndex,
                  error,
                );
              }
            }

            this.totalUnique = allKeys.size;
            this.ready = true;
            this.dispatchCounts();
          },

          // Load next page into the DOM
          async loadNext() {
            if (this.loading || this.currentPage >= this.totalPages) {
              return;
            }

            this.loading = true;
            const nextPage = this.currentPage + 1;
            let doc;

            try {
              if (this.prefetchedDocs[nextPage]) {
                // reuse prefetched DOM
                doc = this.prefetchedDocs[nextPage];
              } else {
                // fallback - fetch normally
                const url = new URL(window.location.href);
                url.searchParams.set('page', nextPage);
                url.searchParams.set('section_id', this.sectionId);

                const response = await fetch(url.toString());
                const html = await response.text();
                doc = new DOMParser().parseFromString(html, 'text/html');
              }

              const mount = document.getElementById('items-grid');
              let appended = 0;

              const promoNodes = doc.querySelectorAll(
                '#items-grid .grid-item:not([data-group-key])',
              );
              promoNodes.forEach((node) => {
                mount.insertAdjacentHTML('beforeend', node.outerHTML);
              });

              const keysFromDoc = this.extractGroupKeys(doc);
              keysFromDoc.forEach((key) => {
                if (!this.seenKeys.has(key)) {
                  this.seenKeys.add(key);

                  // append the first node with this key
                  const node = doc.querySelector(
                    `#items-grid .grid-item[data-group-key="${key}"]`,
                  );
                  if (node) {
                    mount.insertAdjacentHTML('beforeend', node.outerHTML);
                    appended++;
                  }
                }
              });

              this.currentPage = nextPage;

              if (appended > 0) {
                this.renderedCount = this.seenKeys.size;
                this.dispatchCounts();

                // Refresh Yotpo widgets for newly loaded products
                requestAnimationFrame(() => {
                  if (window.yotpo) {
                    window.yotpo.refreshWidgets();
                  }
                });
              }
            } catch (error) {
              console.error('Load more failed', error);
            } finally {
              this.loading = false;
            }
          },

          // Emit custom event so header/footer counts update
          dispatchCounts() {
            const detail = {
              sectionId: this.sectionId,
              rendered: this.renderedCount,
              total: this.totalUnique,
            };
            window.dispatchEvent(new CustomEvent('dedup:counts', { detail }));
          },
        }),
      );

      // Bind global listener once
      if (!window.__dedupCountsBound) {
        window.__dedupCountsBound = true;

        window.addEventListener(
          'dedup:counts',
          (event) => {
            const { sectionId, rendered, total } = event.detail || {};
            if (typeof total !== 'number') {
              return;
            }

            const shown = Math.min(rendered, total);

            // Footer - "Showing X of Y"
            const footer = document.getElementById(
              `btn-pagination-${sectionId}`,
            );
            const span = footer?.querySelector('[data-showing-label]');
            if (span) {
              const tpl = span.dataset.tpl || 'Showing __ITEM__ of __TOTAL__';
              span.textContent = tpl
                .replace('__ITEM__', shown)
                .replace('__TOTAL__', total);
              span.hidden = false;
            }

            // Header - "X products" or "X of Y products"
            const header = document.getElementById('ProductCount_header');
            const sr = document.getElementById('ProductCount');

            if (header) {
              const { mode, tplSimple, tplRange } = header.dataset;

              let text;
              if (mode === 'simple') {
                text = tplSimple
                  ? tplSimple.replace('__COUNT__', total)
                  : `${total}`;
              } else {
                text = tplRange
                  ? tplRange
                      .replace('__COUNT_SHOWN__', shown)
                      .replace('__COUNT_TOTAL__', total)
                  : `Showing ${shown} of ${total}`;
              }

              header.textContent = text;
              header.hidden = false;

              if (sr) {
                sr.textContent = text;
              }
            }
          },
          { passive: true },
        );
      }
    });
  });
}
